import { FC, useMemo } from 'react';

import { ColumnTable, Entry } from '@/components/ColumnTable';
import { useMetric } from '@/hooks/dbtStrategy/useMetric';
import { useLinkToExplorer } from '@/hooks/links';
import { useUrlProjectParams } from '@/hooks/params';
import { useCommonLineageEntries } from '@/hooks/useCommonLineageEntries';
import { PanelDetails, ResourceDetailsParams } from './panelDetailTypes';
import { TagLinks } from './TagLinks';

export const useMetricDetailsPanel = ({
  uniqueId,
  projectName,
}: ResourceDetailsParams): PanelDetails => {
  const metricQuery = useMetric(uniqueId);
  const projectScope = useUrlProjectParams();

  const commonEntries = useCommonLineageEntries(projectName);

  const getGroupLink = useLinkToExplorer('resourceDetails');

  return useMemo(() => {
    const metric = metricQuery.data.definition;
    if (!metric) {
      return { ...metricQuery, rows: null, description: null };
    }

    const rows: Entry[] = [
      ...commonEntries,
      { key: 'Type', data: metric.type },
      {
        key: 'Tags',
        data: <TagLinks tags={metric.tags} />,
      },
      {
        key: 'Group',
        data: metric.group,
        link: getGroupLink({
          ...projectScope,
          dbtUniqueId: `group.${uniqueId.packageName}.${metric.group}`,
        }),
      },
      { key: 'Expression', data: metric.typeParams?.expr },
      { key: 'Grain To Date', data: metric.typeParams?.grain_to_date },
      { key: 'Window Granularity', data: JSON.stringify(metric.typeParams?.window) },
      { key: 'Measure', data: metric.typeParams?.measure?.name },
      { key: 'Measures', data: metric.typeParams?.measures?.name },
      { key: 'Numerator', data: metric.typeParams?.numerator?.name },
      { key: 'Denominator', data: metric.typeParams?.denominator?.name },
      { key: 'Package', data: uniqueId.packageName },
    ];

    return { ...metricQuery, rows, description: metric.description };
  }, [commonEntries, getGroupLink, metricQuery, projectScope, uniqueId.packageName]);
};

export const MetricDetailsTable: FC<ResourceDetailsParams> = ({
  className,
  children,
  compact,
  ...props
}) => {
  const metricDetails = useMetricDetailsPanel(props);

  return (
    <div className={className}>
      {children?.(metricDetails)}
      <ColumnTable
        tableEntries={metricDetails.rows}
        compact={compact}
        isLoading={metricDetails.fetching}
      />
    </div>
  );
};
